import {
  keepPreviousData,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query";

import {
  fetchMySchedule,
  fetchScheduleDayCounts,
} from "@/services/frontendServices/scheduleServices";
import type {
  MyScheduleResponseDto,
  ScheduleDayCountsResponseDto,
} from "@/types";

const IDLE_REFETCH_MS = 5 * 60 * 1000;
const ACTIVE_REFETCH_MS = 30 * 1000;

/**
 * The analysis moves on the agent's clock, not the rep's, so the card polls for
 * it — but only on today, and only while a row can still change. A day the rep
 * is browsing can hold rows that will never be analyzed, and those must not
 * hold the schedule at the fast interval.
 */
export const scheduleRefetchInterval = (
  data: MyScheduleResponseDto | undefined,
  isToday: boolean
): number =>
  isToday &&
  data?.items.some(
    (item) =>
      item.analysisState === "pending" || item.analysisState === "processing"
  )
    ? ACTIVE_REFETCH_MS
    : IDLE_REFETCH_MS;

/**
 * Every entry hangs off `all`, so the `invalidateQueries({ queryKey: all })`
 * calls in usePrep and useAccountPrep refresh each cached day *and* the
 * navigator's counts without either hook knowing the navigator exists.
 */
export const myScheduleKeys = {
  all: ["mySchedule"] as const,
  day: (date?: string) => ["mySchedule", date ?? "today"] as const,
  counts: (from: string, to: string) =>
    ["mySchedule", "counts", from, to] as const,
};

export const useMySchedule = (
  date?: string,
  options?: Partial<UseQueryOptions<MyScheduleResponseDto, Error>>
) =>
  useQuery<MyScheduleResponseDto, Error>({
    queryKey: myScheduleKeys.day(date),
    queryFn: () => fetchMySchedule(date),
    // Navigating days swaps the key. Without this the card would blank out to
    // skeletons on every arrow press; instead the previous day stays put,
    // dimmed, until the next one lands.
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
    refetchInterval: IDLE_REFETCH_MS,
    refetchOnWindowFocus: true,
    ...options,
  });

export const useScheduleDayCounts = (
  from: string,
  to: string,
  options?: Partial<UseQueryOptions<ScheduleDayCountsResponseDto, Error>>
) =>
  useQuery<ScheduleDayCountsResponseDto, Error>({
    queryKey: myScheduleKeys.counts(from, to),
    queryFn: () => fetchScheduleDayCounts(from, to),
    staleTime: 60 * 1000,
    ...options,
  });
